import React from "react";
import { DayPill } from "../../atoms/DayPill";
import { Dropdown } from "../Dropdown";
import { NumberStepper } from "../NumberStepper";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { WEEKDAYS } from "../../organisms/DeliverStory/types";
import {
  HOUR_OPTIONS,
  TIMEZONE_OPTIONS,
  UNIT_OPTIONS,
  WEEKDAY_FULL,
  WEEKDAY_LABELS,
} from "../../../utils/scheduleConstants";
import type { BaseScheduleErrors } from "../../../utils/scheduleTypes";
import {
  DayRow,
  EveryRow,
  Field,
  FieldLabel,
  HelperText,
  PickerRoot,
  StepperSlot,
  TwoCol,
  UnitSlot,
} from "./SchedulePicker.styles";
import type { SchedulePickerProps } from "./SchedulePicker.types";

const showError = (
  show: boolean,
  errors: BaseScheduleErrors,
  key: keyof BaseScheduleErrors,
): boolean => show && Boolean(errors[key]);

/**
 * Stateless schedule sub-form: "Schedule every", "Repeat on" (week-only),
 * "Refresh at" (hour + timezone). Single source of truth for schedule UI;
 * consumed by both DeliverStory and the connector ScheduleRefreshModal.
 *
 * Parent owns config + validation + dropdown state — this is pure
 * presentation, so the same instance drops into any modal.
 */
export const SchedulePicker: React.FC<SchedulePickerProps> = ({
  value,
  errors,
  showErrors,
  openDropdown,
  onChange,
  onToggleWeekday,
  onOpenDropdown,
  onCloseDropdown,
  timezoneOptions = TIMEZONE_OPTIONS,
  className,
}) => {
  const isOpen = (key: "unit" | "hour" | "timezone") => openDropdown === key;

  const unitPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: isOpen("unit"),
    onOutsideClick: onCloseDropdown,
  });
  const hourPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: isOpen("hour"),
    onOutsideClick: onCloseDropdown,
  });
  const tzPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: isOpen("timezone"),
    onOutsideClick: onCloseDropdown,
  });

  return (
    <PickerRoot className={className}>
      <Field>
        <FieldLabel>Schedule every</FieldLabel>
        <EveryRow>
          <StepperSlot>
            <NumberStepper
              value={value.every}
              min={1}
              max={99}
              invalid={showError(showErrors, errors, "every")}
              ariaLabel="Schedule interval"
              onChange={(n) => onChange({ every: n })}
            />
          </StepperSlot>
          <UnitSlot>
            <Dropdown
              value={value.unit}
              options={UNIT_OPTIONS}
              open={isOpen("unit")}
              triggerRef={unitPortal.triggerRef}
              menuRef={unitPortal.menuRef}
              menuRect={unitPortal.rect}
              size="md"
              ariaLabel="Schedule unit"
              onToggle={() => onOpenDropdown("unit")}
              onSelect={(u) => {
                onChange({ unit: u });
                onCloseDropdown();
              }}
            />
          </UnitSlot>
        </EveryRow>
        {showError(showErrors, errors, "every") && (
          <HelperText $error>{errors.every}</HelperText>
        )}
      </Field>

      {value.unit === "week" && (
        <Field>
          <FieldLabel>Repeat on</FieldLabel>
          <DayRow>
            {WEEKDAYS.map((day) => (
              <DayPill
                key={day}
                letter={WEEKDAY_LABELS[day]}
                ariaLabel={WEEKDAY_FULL[day]}
                selected={value.repeatOn.includes(day)}
                onClick={() => onToggleWeekday(day)}
                size="lg"
              />
            ))}
          </DayRow>
          {showError(showErrors, errors, "repeatOn") && (
            <HelperText $error>{errors.repeatOn}</HelperText>
          )}
        </Field>
      )}

      <Field>
        <FieldLabel>Refresh at</FieldLabel>
        <TwoCol>
          <Dropdown
            value={value.refreshHour === null ? null : String(value.refreshHour)}
            options={HOUR_OPTIONS}
            open={isOpen("hour")}
            triggerRef={hourPortal.triggerRef}
            menuRef={hourPortal.menuRef}
            menuRect={hourPortal.rect}
            size="md"
            placeholder="Pick a time"
            invalid={showError(showErrors, errors, "refreshHour")}
            ariaLabel="Refresh hour"
            onToggle={() => onOpenDropdown("hour")}
            onSelect={(v) => {
              onChange({ refreshHour: Number(v) });
              onCloseDropdown();
            }}
          />
          <Dropdown
            value={value.timezone}
            options={timezoneOptions}
            open={isOpen("timezone")}
            triggerRef={tzPortal.triggerRef}
            menuRef={tzPortal.menuRef}
            menuRect={tzPortal.rect}
            size="md"
            showScrollbar
            placeholder="Timezone"
            invalid={showError(showErrors, errors, "timezone")}
            ariaLabel="Timezone"
            onToggle={() => onOpenDropdown("timezone")}
            onSelect={(v) => {
              onChange({ timezone: v });
              onCloseDropdown();
            }}
          />
        </TwoCol>
        {(showError(showErrors, errors, "refreshHour") ||
          showError(showErrors, errors, "timezone")) && (
          <HelperText $error>
            {errors.refreshHour ?? errors.timezone}
          </HelperText>
        )}
      </Field>
    </PickerRoot>
  );
};

export default SchedulePicker;
